"use server";

import { z } from "zod";
import { AuthError } from "next-auth";
import { signIn, signOut } from "@/lib/auth";
import type { OAuthProvider } from "@/lib/auth-providers";
import { createEmailCode, sendEmailCode } from "@/lib/email-code";

function normalizeEmail(value: FormDataEntryValue | null): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

const emailSchema = z.email({ error: "올바른 이메일을 입력해 주세요." });

// 1단계 상태: 코드 발송 성공 시 입력한 이메일을 들고 코드 입력 화면으로 전환.
export type RequestCodeState =
  | { ok: true; email: string }
  | { error: string }
  | undefined;

// 1단계: 이메일 입력 → 6자리 인증코드 발송.
// 보안상 이메일 가입 여부는 노출하지 않는다(있든 없든 동일하게 발송 처리).
export async function requestEmailCode(
  _prev: RequestCodeState,
  formData: FormData,
): Promise<RequestCodeState> {
  const email = normalizeEmail(formData.get("email"));
  const parsed = emailSchema.safeParse(email);
  if (!parsed.success) {
    return { error: parsed.error.issues[0]?.message ?? "이메일을 확인해 주세요." };
  }

  try {
    const code = await createEmailCode(email);
    await sendEmailCode(email, code);
  } catch (e) {
    console.error("[login] 인증코드 발송 실패:", e);
    return { error: "코드 발송에 실패했어요. 잠시 후 다시 시도해 주세요." };
  }

  return { ok: true, email };
}

export type VerifyCodeState = { error: string } | undefined;

// 2단계: 이메일+코드 검증 → 로그인(없으면 가입). 성공 시 /today로 리다이렉트.
export async function verifyEmailCodeAndLogin(
  _prev: VerifyCodeState,
  formData: FormData,
): Promise<VerifyCodeState> {
  const email = normalizeEmail(formData.get("email"));
  const code = String(formData.get("code") ?? "").trim();
  if (!/^\d{6}$/.test(code)) {
    return { error: "6자리 숫자 코드를 입력해 주세요." };
  }

  // 성공 시 signIn이 redirect 예외를 throw → 그대로 전파. 실패(AuthError)만 처리.
  try {
    await signIn("email-code", { email, code, redirectTo: "/today" });
  } catch (error) {
    if (error instanceof AuthError) {
      return { error: "인증코드가 올바르지 않거나 만료됐어요." };
    }
    throw error;
  }
  return undefined;
}

// 소셜 로그인/가입: 해당 제공자의 OAuth 플로우로 리다이렉트한다.
// signIn은 성공 시 redirect 예외를 throw 하므로 그대로 전파시킨다.
export async function signInWithProvider(provider: OAuthProvider) {
  await signIn(provider, { redirectTo: "/today" });
}

// 로그아웃: 세션을 비우고 로그인 화면으로 보낸다.
export async function logout() {
  await signOut({ redirectTo: "/login" });
}
